import { useValue } from "@legendapp/state/react";
import { Popover, PopoverContent } from "@repo/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@repo/ui/tooltip";
import type { Ref } from "react";
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { blocks$ } from "~/stores/blocks/blocks.crud";
import type { Block } from "~/stores/blocks/blocks.types";
import { boards$ } from "~/stores/boards/boards.crud";
import type { Board } from "~/stores/boards/boards.types";
import { datasets$ } from "~/stores/datasets/datasets.crud";
import type { Dataset } from "~/stores/datasets/datasets.types";
import { datasources$ } from "~/stores/datasources/datasources.crud";
import type { Datasource } from "~/stores/datasources/datasources.types";
import { MentionPillIcon } from "./mention-icon";
import {
  flattenMentionItems,
  MENTION_KIND_LABEL,
  type MentionItem,
  searchMentions,
} from "./mention-search";

export type MentionPopoverHandle = {
  /**
   * Forwarded by the editor's `suggestion.onKeyDown`. Returns `true` to consume
   * the key and prevent Tiptap's default handling (e.g. submitting on Enter
   * when the popover has items).
   */
  onKeyDown: (event: KeyboardEvent) => boolean;
};

export type MentionPopoverProps = {
  /**
   * React 19 ref-as-prop. The editor's `mention.suggestion.render` owns this
   * via `createRef` and reads `.current.onKeyDown(...)` to commit selection
   * on Enter/Tab. Passing as a regular prop sidesteps Tiptap's flaky
   * `ReactRenderer.ref` callback under React 19 (ueberdosis/tiptap#6110).
   */
  ref?: Ref<MentionPopoverHandle>;
  query: string;
  /** Tiptap-supplied virtual anchor — returns the caret rect or null while inactive. */
  clientRect: (() => DOMRect | null) | null | undefined;
  /** Called by Tiptap's controller; we forward the resolved item back into the editor. */
  onSelect: (item: MentionItem) => void;
  /**
   * Called when Base UI reports a dismiss intent (outside press, escape,
   * focus-out). The owner is expected to exit Tiptap's suggestion plugin so
   * the popover doesn't immediately reopen on the next edit.
   */
  onDismiss: () => void;
};

/**
 * Popover anchored to the caret while a `@`-mention suggestion is active.
 *
 * Tiptap owns:
 * - the trigger character + query (via `mention.suggestion`)
 * - keyboard event delivery (via `onKeyDown` forwarded to the imperative ref)
 *
 * This component owns:
 * - the result list (filtered from in-memory Legend State stores)
 * - the highlighted index
 * - selection commit (`onSelect` calls Tiptap's `command(item)`)
 *
 * Items are sourced live from the existing `blocks$` / `datasets$` /
 * `datasources$` CRUD stores so search has zero network cost. The popover
 * stays mounted while the suggestion is active — Tiptap unmounts it on exit.
 */
export const MentionPopover = ({
  ref,
  query,
  clientRect,
  onSelect,
  onDismiss,
}: MentionPopoverProps) => {
  const blocksMap = useValue(blocks$) as Record<string, Block> | undefined;
  const boardsMap = useValue(boards$) as Record<string, Board> | undefined;
  const datasetsMap = useValue(datasets$) as
    | Record<string, Dataset>
    | undefined;
  const datasourcesMap = useValue(datasources$) as
    | Record<string, Datasource>
    | undefined;

  const groups = useMemo(() => {
    const blocks = Object.values(blocksMap ?? {}).filter(
      (b): b is Block => !!b && !!b.id,
    );
    const boards = Object.values(boardsMap ?? {}).filter(
      (b): b is Board => !!b && !!b.id,
    );
    const datasets = Object.values(datasetsMap ?? {}).filter(
      (d): d is Dataset => !!d && !!d.id,
    );
    const datasources = Object.values(datasourcesMap ?? {}).filter(
      (d): d is Datasource => !!d && !!d.id,
    );
    return searchMentions({ query, blocks, boards, datasets, datasources });
  }, [query, blocksMap, boardsMap, datasetsMap, datasourcesMap]);

  const flat = useMemo(() => flattenMentionItems(groups), [groups]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Reset selection whenever the result set changes — keeps the cursor on a
  // valid row even after the query narrows.
  useEffect(() => {
    setSelectedIndex(0);
  }, [flat.length, query]);

  useEffect(() => {
    const el = itemRefs.current[selectedIndex];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const commit = (idx: number) => {
    const item = flat[idx];
    if (item) onSelect(item);
  };

  // Always-fresh state for the imperative handle. The handle itself is stable
  // (created once with empty deps); each method reads `.current` so it never
  // closes over a stale `flat` / `selectedIndex` from an earlier render.
  const stateRef = useRef({ flat, selectedIndex, commit, setSelectedIndex });
  stateRef.current = { flat, selectedIndex, commit, setSelectedIndex };

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: (event) => {
        const { flat, selectedIndex, commit, setSelectedIndex } =
          stateRef.current;
        if (event.key === "ArrowDown") {
          if (flat.length === 0) return false;
          setSelectedIndex((i) => (i + 1) % flat.length);
          return true;
        }
        if (event.key === "ArrowUp") {
          if (flat.length === 0) return false;
          setSelectedIndex((i) => (i - 1 + flat.length) % flat.length);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          // No items: let Enter fall through so the chat sends the message
          // (typed `@foo` becomes literal text). Tab also falls through.
          if (flat.length === 0) return false;
          commit(selectedIndex);
          return true;
        }
        return false;
      },
    }),
    [],
  );

  // Build a virtual anchor compatible with Base UI Popover's `anchor` prop.
  // Tiptap's `clientRect` returns the caret DOMRect; we wrap it so each open/
  // resize cycle reads the latest rect.
  const anchor = useMemo(() => {
    if (!clientRect) return null;
    return {
      getBoundingClientRect: () => clientRect() ?? new DOMRect(0, 0, 0, 0),
    };
  }, [clientRect]);

  if (!anchor) return null;

  let cursor = 0;

  return (
    <Popover
      open={true}
      onOpenChange={(open) => {
        // Base UI fires `false` for outside press, escape, and focus-out.
        // Tiptap also handles Escape via its suggestion plugin keydown, but
        // the dispatch here is idempotent — `exitSuggestion` is a no-op when
        // the plugin is already inactive.
        if (!open) onDismiss();
      }}
    >
      <PopoverContent
        anchor={anchor}
        side="top"
        align="start"
        sideOffset={6}
        className="flex max-h-72 w-72 flex-col gap-0 overflow-hidden p-0"
        initialFocus={false}
        finalFocus={false}
      >
        <TooltipProvider delay={0}>
          <div
            role="listbox"
            aria-label="Mention suggestions"
            className="no-scrollbar flex flex-col overflow-y-auto p-1"
          >
            {flat.length === 0 ? (
              <div className="px-2 py-3 text-center text-muted-foreground text-xs">
                No matches
              </div>
            ) : (
              groups.map((group) => {
                if (group.items.length === 0) return null;
                return (
                  <div key={group.kind} className="flex flex-col gap-0.5 py-1">
                    <div className="px-2 pb-1 font-normal text-muted-foreground text-xs">
                      {MENTION_KIND_LABEL[group.kind]}
                    </div>
                    {group.items.map((item) => {
                      const idx = cursor;
                      cursor++;
                      const isHighlighted = idx === selectedIndex;
                      const button = (
                        <button
                          key={item.key}
                          type="button"
                          role="option"
                          aria-selected={isHighlighted}
                          ref={(el) => {
                            itemRefs.current[idx] = el;
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            commit(idx);
                          }}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          data-highlighted={isHighlighted ? "" : undefined}
                          className="flex min-h-7 w-full cursor-default select-none items-center gap-2 rounded-md px-2 py-1 text-left text-xs/relaxed outline-hidden data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                        >
                          <MentionPillIcon
                            id={item.id}
                            kind={item.kind}
                            className="size-3.5 text-muted-foreground"
                          />
                          <span className="min-w-0 flex-1 truncate">
                            {item.label}
                          </span>
                        </button>
                      );

                      if (item.kind !== "block") return button;

                      const block = blocksMap?.[item.id];
                      const boardName = block?.boardId
                        ? boardsMap?.[block.boardId]?.name?.trim() ||
                          "Untitled board"
                        : undefined;
                      const datasetName = block?.datasetId
                        ? datasetsMap?.[block.datasetId]?.name?.trim() ||
                          "Untitled dataset"
                        : undefined;

                      if (!boardName && !datasetName) return button;

                      return (
                        <Tooltip key={item.key} open={isHighlighted}>
                          <TooltipTrigger render={button} />
                          <TooltipContent
                            side="right"
                            align="start"
                            sideOffset={12}
                          >
                            <div className="flex flex-col gap-0.5">
                              {boardName && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Board{" "}
                                  </span>
                                  {boardName}
                                </div>
                              )}
                              {datasetName && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Dataset{" "}
                                  </span>
                                  {datasetName}
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        </TooltipProvider>
      </PopoverContent>
    </Popover>
  );
};
